import { Settings } from 'luxon'
import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'

import { resetDataManagementState } from '@Store/slices/dataManagementSlice'

import EnvIndicator from './EnvIndicator'
import Tooltips from '../components/common/tooltips'
import LeftMenu from '../components/menu/main-menu'
import NavigateBillingModal from '../components/product/navigate-billing-modal'
import type { RootState } from '../store'
import { useLazyMeQuery } from '../store/api'
import { useGetLatestExportStatusQuery } from '../store/api/product'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { unsetTokens } from '../store/slices/authSlice'
import {
  setExportStatus,
  setProductCompetition,
} from '../store/slices/settingsSlice'
import { PremiumFeatures } from '../store/types/billing'
import ExportReadyModal from '../utils/export/export-ready-modal'
import { useObserveBody } from '../utils/hooks/use-observe-body'

function isOnboardingCompleted(user: any) {
  return user?.is_onboarding_completed === 1
}

function isOnboardingRoute(pathname: string) {
  return pathname.includes('onboarding')
}

export default function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  useObserveBody(document.body)
  const { boot, update } = useIntercom()
  const authSelector = useAppSelector((state: RootState) => state.auth)
  const dispatch = useAppDispatch()
  const [fetcher, { currentData, isError, isLoading, isSuccess }] =
    useLazyMeQuery({
      refetchOnFocus: false,
    })

  const startPollingForExport = useAppSelector(
    (state) => state.settings.is_export_ongoing,
  )

  const { currentData: exportData } = useGetLatestExportStatusQuery(undefined, {
    pollingInterval: startPollingForExport ? 3000 : undefined,
    refetchOnMountOrArgChange: true,
  })

  useEffect(() => {
    if (!exportData) {
      return
    }
    const isExportOngoing = exportData.is_done === 0

    dispatch(
      setExportStatus({
        is_export_ongoing: isExportOngoing,
        ...(!isExportOngoing
          ? {
              resultCode: exportData.is_done,
              reportId: exportData.id,
            }
          : {}),
      }),
    )
  }, [exportData])

  useEffect(() => {
    if (!currentData && authSelector.access_token && !isError && !isLoading) {
      fetcher()
        .unwrap()
        .then((response) => {
          if (
            !isOnboardingCompleted(response) &&
            isOnboardingRoute(location.pathname)
          ) {
            navigate('/onboarding')
          }
        })
    } else {
      dispatch(unsetTokens())
      dispatch(resetDataManagementState())
      if (CURRENT_ENV === 'dev' || CURRENT_ENV === 'test') {
        navigate('/login')
      } else {
        window.location.replace(`${WORDPRESS_HOST}/login`)
      }
    }
  }, [])

  useEffect(() => {
    if (currentData && location.pathname !== '/billing/plans') {
      if (
        currentData?.is_in_trial === 1 &&
        [
          'payment_problem_panel_disabled',
          'expired',
          'churned',
          'uninstalled',
        ].includes(currentData.status)
      ) {
        navigate('/billing/plans')
      }
    }
  }, [location, currentData])

  useEffect(() => {
    if (isSuccess && currentData) {
      if (!currentData?.logged_in_as && currentData?.intercom_user_hash) {
        update({
          userHash: currentData.intercom_user_hash,
          name: currentData.name,
          email: currentData.email,
          userId: currentData.id.toString(),
          createdAt: currentData.date_registered,
          customAttributes: {
            ismine_url: currentData.url,
            phone: currentData.phone,
            is_channel_based: Boolean(
              currentData.monitoring_source === 'PRISYNC_ENGINE',
            ).toString(),
          },
        })
      } else {
        boot()
      }
      if (
        currentData.package_type &&
        !PremiumFeatures.includes(currentData.package_type)
      ) {
        dispatch(setProductCompetition(true))
      }
      Settings.defaultZone = currentData.timezone
    }
  }, [currentData, isSuccess, boot])

  return (
    <EnvIndicator>
      <LeftMenu />
      {authSelector.access_token && isSuccess && <Outlet />}
      <ExportReadyModal />
      <Tooltips />
      <NavigateBillingModal />
    </EnvIndicator>
  )
}
